/**
 * Cache invalidation for the project-wide approval chain mutations — creating with
 * "apply to existing folders", editing, and deleting.
 *
 * Each of these changes data the `['project', id, 'approval-chains']` definition
 * queries do not cover:
 *   - the folder grid reads `['project', id, 'folders', folderId, 'files']` and
 *     gates bulk Approve/Unapprove on each file's `workflowProgress`,
 *   - folder list/detail rows read the rolled-up `approvalProgress`,
 *   - any open asset detail reads `['project', id, 'asset-approval-chain', assetId]`,
 *   - the asset page's approve toggle and Details status read the file's own `status`
 *     from `['project', id, 'files', 'detail', fileId]`, which a delete or an edit can
 *     move when it reopens or completes an asset's chain.
 *
 * Without this bust those entries stay fresh for the global 5-minute `staleTime`
 * (`lib/config/query-client.ts`). After an apply-to-existing the grid keeps
 * serving a pre-chain snapshot in which `workflowProgress` is null and both bulk
 * actions render greyed out; after a delete the asset keeps showing a chain that
 * no longer exists, offering an unassign that 404s.
 *
 * These actions are project-wide (they can touch assets in any folder), so we
 * invalidate the whole `['project', id, 'folders']` prefix rather than resolving
 * specific folders — unlike the folder-scoped assignment mutations.
 */
import type { QueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';

export function invalidateProjectChainAssignments(
  queryClient: QueryClient,
  projectId: string,
): void {
  // `list`/`detail`/`files` all start `['project', projectId, 'folders']`; the
  // 3-element prefix covers every one of them.
  void queryClient.invalidateQueries({
    queryKey: PROJECT_FOLDER_QUERY_KEYS.list(projectId).slice(0, 3),
  });

  void queryClient.invalidateQueries({
    queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.namespace(projectId),
  });

  void queryClient.invalidateQueries({
    queryKey: PROJECT_FILE_QUERY_KEYS.details(projectId),
  });
}
